
import _ from 'lodash';
import moment from 'moment';
import {
  LANGUAGE,
  DATE_FORMAT_CZ,
  DATETIME_FORMAT_CZ,
  DATETIME_FORMAT_FULL_CZ,
  DATE_FORMAT_EN,
  DATETIME_FORMAT_EN,
  DATETIME_FORMAT_FULL_EN,
  VAT_MULTIPLICATOR
} from 'shared/constants';

export const formatDateTime = (str, language = 'cs', withSeconds = false) =>
  moment(str).format(
    language === 'en'
      ? (withSeconds ? DATETIME_FORMAT_FULL_EN : DATETIME_FORMAT_EN)
      : (withSeconds ? DATETIME_FORMAT_FULL_CZ : DATETIME_FORMAT_CZ)
  );

export const formatDate = (str, language = 'cs') =>
  moment(str).format(language === 'en' ? DATE_FORMAT_EN : DATE_FORMAT_CZ);

export const createMoneyFormatter = (language = 'cs') => (value, currency = 'CZK', digits = 0) => {
  const opts = {
    style: 'currency',
    currency,
    maximumFractionDigits: digits,
    minimumFractionDigits: digits
  };

  if (typeof Intl === 'undefined') {
    return Math.round(value);
  }

  return new Intl.NumberFormat(language, opts).format(value);
};

export const createVatPrice = price => Math.round(price * VAT_MULTIPLICATOR * 100) / 100;

export const createMoneyConvertor = (language = 'cs', exchangeRateMap = {}) => {
  const format = createMoneyFormatter(language);
  return (value, sourceCurrency, targetCurrency, digits = 0) => {
    targetCurrency = targetCurrency || sourceCurrency;
    if (exchangeRateMap[sourceCurrency] && exchangeRateMap[sourceCurrency][targetCurrency]) {
      return format(value * exchangeRateMap[sourceCurrency][targetCurrency], targetCurrency, digits);
    }

    return format(value, sourceCurrency, digits);
  };
};

export const createMoneyRangeConvertor = (t, language = 'cs', exchangeRateMap = {}) => {
  const format = createMoneyFormatter(language);
  return (from, to, sourceCurrency, targetCurrency, hideRateUpTo = false, digits = 0) => {
    targetCurrency = targetCurrency || sourceCurrency;

    if (hideRateUpTo) {
      return t('common:job.rateFrom', {
        amount: format(from * exchangeRateMap[sourceCurrency][targetCurrency], targetCurrency, digits)
      });
    }

    if (exchangeRateMap[sourceCurrency] && exchangeRateMap[sourceCurrency][targetCurrency]) {
      if (to > 0 && from !== to) {
        return `${
          format(from * exchangeRateMap[sourceCurrency][targetCurrency], targetCurrency, digits)
        } - ${
          format(to * exchangeRateMap[sourceCurrency][targetCurrency], targetCurrency, digits)
        }`;
      }

      return format(from * exchangeRateMap[sourceCurrency][targetCurrency], targetCurrency, digits);
    }

    return format(from, sourceCurrency, digits);
  };
};

export const formatZip = (zip) => {
  const raw = zip.toString().replace(/[ ]+/g, '');
  return `${raw.slice(0, 3)} ${raw.slice(3)}`;
};

export const formatRateRange = (minRate, maxRate, hasOneRate, language, currency) => {
  const formatMoney = createMoneyFormatter(language);
  if (minRate !== maxRate) {
    return minRate > 10000
      ? language === LANGUAGE.EN
        ? `${formatMoney(~~(minRate / 1000), currency)} - ${formatMoney(maxRate, currency).replace(currency, '').trim()}${!hasOneRate ? ' *' : ''}`
        : `${~~(minRate / 1000)} - ${formatMoney(maxRate, currency)}${!hasOneRate ? ' *' : ''}`
      : `${formatMoney(minRate, currency)} - ${formatMoney(maxRate, currency)}${!hasOneRate ? ' *' : ''}`;
  }

  return `${formatMoney(minRate, currency)}${!hasOneRate ? ' *' : ''}`;
};

export const formatJobRateRange = (t, employmentTypes = [], language, currency) => {
  const hasOneRate = _.uniq(employmentTypes.map(et => et.monthlyRate)).length <= 1;
  const hasRateUpToHidden = employmentTypes.some((et) => et.hideRateUpTo);
  const minRate = Math.min(...(employmentTypes.map(et => et.monthlyRate)));
  const maxRate = Math.max(...(employmentTypes.map(et => et.monthlyRateUpTo)));

  if (hasRateUpToHidden) {
    return t('common:job.rateFrom', {
      amount: formatRateRange(minRate, minRate, false, language, currency)
    });
  }

  return formatRateRange(minRate, maxRate, hasOneRate, language, currency);
};

export default {
  formatDate,
  formatDateTime,
  createMoneyFormatter,
  createMoneyConvertor,
  createVatPrice,
  formatZip
};
